import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { generateBatchSaenggibuStream, getFriendlyBatchErrorMessage } from '../../../api/chatApi';
import { cancelSaenggibuGeneration, getSaenggibuGenerationJobStatus } from '../../../api/saenggibuApi';
import BatchLoadingMiniGame from '../../common/BatchLoadingMiniGame';
import './Step4_Processing.css';

const createBatchGenerationJobId = () => (
  `batch_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);

// 스트림이 완전히 끊긴 뒤, 짧은 상태 조회로 서버 완료를 기다리는 복구 한도
const RECOVERY_MAX_WAIT_MS = 10 * 60_000;
const RECOVERY_POLL_INTERVAL_MS = 10_000;

// 진행률 게이지 정책 (검사기와 동일한 축: 8% 시작, 서버 완료 인원 비율 × 88, 완료 전 최대 96%)
const PROGRESS_BASE = 8;
const PROGRESS_SPAN = 88;
const PROGRESS_MAX_BEFORE_COMPLETE = 96;
// 서버가 "지금 작성 중인 인원" 을 알려주면, 그 인원이 끝났을 때 도달할 지점의 85% 까지만 시간에 따라 채운다.
// 청크가 동시에 돌아 chunk_done 이 끝에 몰려도 게이지가 멈춰 보이지 않고, 실제 완료 전엔 그 지점을 넘지 않는다.
const IN_FLIGHT_FILL_RATIO = 0.85;
const IN_FLIGHT_MIN_HEADROOM = 4;
// 상한까지 1 - e^(-t/τ) 로 접근 (τ=20초: 20초 63%, 40초 86%, 60초 95%). 상한에 닿아 멈추는 순간이 없다.
const IN_FLIGHT_FILL_TAU_SECONDS = 20;
// 구버전 백엔드(in_flight 없음) 호환: 6초에 1%p, 최대 +8%p
const LEGACY_CREEP_MAX = 8;
const LEGACY_CREEP_SECONDS_PER_POINT = 6;

const progressFromCounts = (completed, total) => (
  Math.round(PROGRESS_BASE + (completed / total) * PROGRESS_SPAN)
);

/**
 * 서버 인원 카운트 기반으로 다음 게이지 값을 계산한다 (순수 함수, 테스트 대상).
 * @param {{floor:number, inFlight:number|null, total:number, sinceLastEventSeconds:number}} p
 */
export const computeServerBackedProgress = ({ floor, inFlight, total, sinceLastEventSeconds }) => {
  if (typeof inFlight !== 'number' || !(total > 0)) {
    const creep = Math.min(LEGACY_CREEP_MAX, Math.floor(sinceLastEventSeconds / LEGACY_CREEP_SECONDS_PER_POINT));
    return Math.min(PROGRESS_MAX_BEFORE_COMPLETE, floor + creep);
  }
  const headroom = Math.max(IN_FLIGHT_MIN_HEADROOM, Math.round((inFlight / total) * PROGRESS_SPAN * IN_FLIGHT_FILL_RATIO));
  const cap = Math.min(PROGRESS_MAX_BEFORE_COMPLETE, floor + headroom);
  const ratio = 1 - Math.exp(-Math.max(0, sinceLastEventSeconds) / IN_FLIGHT_FILL_TAU_SECONDS);
  return Math.min(cap, Math.round(floor + (cap - floor) * ratio));
};

export const formatEstimatedDuration = (seconds) => {
  if (typeof seconds !== 'number' || !(seconds > 0)) return null;
  const minutes = Math.ceil(seconds / 60);
  return minutes <= 1 ? '약 1분 정도 걸려요' : `약 ${minutes}분 정도 걸려요`;
};

const Step4_Processing = ({ 
  analyzedData, 
  selectedOptions, 
  onNext, 
  onBack, 
  isProcessing, 
  setIsProcessing 
}) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [progress, setProgress] = useState(0);
  const [currentStudent, setCurrentStudent] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isCancellingGeneration, setIsCancellingGeneration] = useState(false);
  // 서버가 알려준 실제 인원 카운트 (검사기와 같은 "N/M명 완료" 헤더용). null 이면 % 표시.
  const [serverCounts, setServerCounts] = useState(null);
  // start 이벤트의 라운드 기반 예상 시간(초)
  const [estimatedSeconds, setEstimatedSeconds] = useState(null);
  const hasStarted = useRef(false); // 중복 실행 방지
  const progressIntervalRef = useRef(null);
  const generationAbortControllerRef = useRef(null);
  const generationCancelRequestedRef = useRef(false);
  const generationJobIdRef = useRef(null);
  const completionTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);
  const generationRunIdRef = useRef(0);
  // 서버가 보내준 실제 진행률(%) 하한선과 마지막 서버 이벤트 시각
  const serverFloorRef = useRef(0);
  const hasServerCountsRef = useRef(false);
  const lastServerEventAtRef = useRef(0);
  // 서버가 보낸 "지금 작성 중인 인원" / 전체 인원 (게이지 상한 계산용)
  const inFlightRef = useRef(null);
  const totalStudentsRef = useRef(0);
  // 지금까지 반영한 최대 완료 인원 (헤더 카운트 역행 방지)
  const lastCompletedRef = useRef(-1);
  // 서버 안내 문구를 시뮬레이션 문구가 덮어쓰지 않도록 보호하는 시각
  const messageHoldUntilRef = useRef(0);

  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgressSimulation = useCallback((studentCount) => {
    stopProgressSimulation();
    const startTime = Date.now();
    setElapsedSeconds(0);
    setProgress(PROGRESS_BASE);
    serverFloorRef.current = PROGRESS_BASE;
    hasServerCountsRef.current = false;
    lastServerEventAtRef.current = Date.now();
    inFlightRef.current = null;
    totalStudentsRef.current = studentCount;
    lastCompletedRef.current = -1;
    messageHoldUntilRef.current = 0;
    setServerCounts(null);
    setEstimatedSeconds(null);
    setCurrentStudent(`${studentCount}명 내용을 정리하고 있어요`);

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedSeconds(elapsed);

      if (hasServerCountsRef.current) {
        // 서버가 청크 단위 실제 진행률을 보내는 중:
        // 완료 인원(floor) 위로, 지금 작성 중인 인원이 끝났을 때 도달할 지점 근처까지만 시간에 따라 채운다.
        const sinceLastEventSeconds = (Date.now() - lastServerEventAtRef.current) / 1000;
        const next = computeServerBackedProgress({
          floor: serverFloorRef.current,
          inFlight: inFlightRef.current,
          total: totalStudentsRef.current,
          sinceLastEventSeconds,
        });
        setProgress(prev => Math.max(prev, next));
        return;
      }

      let nextProgress;
      if (elapsed < 12) {
        nextProgress = 8 + (elapsed / 12) * 30;
      } else if (elapsed < 45) {
        nextProgress = 38 + ((elapsed - 12) / 33) * 40;
      } else if (elapsed < 90) {
        nextProgress = 78 + ((elapsed - 45) / 45) * 14;
      } else {
        nextProgress = Math.min(96, 92 + ((elapsed - 90) / 90) * 4);
      }

      setProgress(prev => Math.max(prev, Math.round(nextProgress), serverFloorRef.current));

      // 서버가 보낸 안내 문구가 표시 중이면 시뮬레이션 문구로 덮어쓰지 않는다
      if (Date.now() < messageHoldUntilRef.current) return;

      if (elapsed < 12) {
        setCurrentStudent(`${studentCount}명 학생 내용을 확인하고 있어요`);
      } else if (elapsed < 45) {
        setCurrentStudent('학생별 생기부 문구를 작성하고 있어요');
      } else if (elapsed < 90) {
        setCurrentStudent('문장을 다듬고 바이트를 맞추고 있어요');
      } else {
        setCurrentStudent('조금 더 걸리고 있어요. 창을 닫지 않으면 결과가 이어서 표시돼요');
      }
    }, 700);
  }, [stopProgressSimulation]);

  // 서버 안내 문구 표시 (시뮬레이션 문구가 holdMs 동안 덮어쓰지 못함)
  const setServerMessage = useCallback((text, holdMs = 12_000) => {
    messageHoldUntilRef.current = Date.now() + holdMs;
    setCurrentStudent(text);
  }, []);

  // 서버 인원 카운트 반영 (스트림 progress, join tick, 상태 조회 복구 공용)
  // 완료 인원은 단조 증가만 허용한다: 복구 폴링/join 에서 뒤늦게 온 옛 스냅샷이 "12명 완료 → 6명 완료" 로
  // 보이게 하지 않는다 (Bugbot 2026-09-20). 옛 스냅샷은 통째로 무시하고 false 를 돌려준다.
  // 반환값: 카운트가 유효하고 최신이어서 반영됐는지
  const applyServerCounts = useCallback(({ completed, total, inFlight }) => {
    if (!(typeof completed === 'number' && typeof total === 'number' && total > 0)) return false;
    const safeCompleted = Math.max(0, Math.min(completed, total));
    if (safeCompleted < lastCompletedRef.current) return false;
    const safeInFlight = typeof inFlight === 'number'
      ? Math.max(0, Math.min(inFlight, total - safeCompleted))
      : null;
    hasServerCountsRef.current = true;
    totalStudentsRef.current = total;
    inFlightRef.current = safeInFlight;
    lastCompletedRef.current = safeCompleted;
    serverFloorRef.current = Math.max(serverFloorRef.current, progressFromCounts(safeCompleted, total));
    setProgress(prev => Math.max(prev, serverFloorRef.current));
    setServerCounts({ completed: safeCompleted, total, inFlight: safeInFlight });
    return true;
  }, []);

  // 서버 스트림 progress 이벤트 → 실제 진행률/안내 문구 반영
  const handleServerProgress = useCallback((event) => {
    if (!isMountedRef.current || !event) return;
    const {
      stage,
      completed_students: completedStudents,
      total_students: totalStudents,
      in_flight_students: inFlightStudents,
      message,
    } = event;

    if (event.type === 'start') {
      if (typeof event.estimated_seconds === 'number' && event.estimated_seconds > 0) {
        setEstimatedSeconds(event.estimated_seconds);
      }
      return;
    }
    if (stage === 'heartbeat') return; // 연결 유지용 신호 (표시는 경과 시간이 담당)
    lastServerEventAtRef.current = Date.now();

    if (stage === 'reconnecting') {
      setServerMessage('연결이 잠시 끊겨 다시 연결하고 있어요. 생성은 서버에서 계속 진행돼요.', 20_000);
      return;
    }
    if (stage === 'joining') {
      // 다른 worker 에서 실행 중인 잡에 합류: 잡 문서에 남은 카운트가 있으면 그대로 보여준다
      const joined = applyServerCounts({
        completed: completedStudents,
        total: totalStudents,
        inFlight: inFlightStudents,
      });
      if (joined) {
        setServerMessage(
          `${totalStudents}명 중 ${completedStudents}명 완료 · 이전 요청의 생성이 이어지고 있어요`,
          20_000
        );
      } else {
        setServerMessage(message || '이전 요청의 생성이 진행 중이에요. 결과를 기다리고 있어요.', 20_000);
      }
      return;
    }

    if (stage === 'chunk_start' || stage === 'chunk_done') {
      const applied = applyServerCounts({
        completed: completedStudents,
        total: totalStudents,
        inFlight: inFlightStudents,
      });
      if (!applied) return;
      const inFlight = inFlightRef.current;
      const inFlightSuffix = inFlight > 0 ? ` · ${inFlight}명 작성 중` : '';
      if (stage === 'chunk_done') {
        setServerMessage(`${totalStudents}명 중 ${completedStudents}명 작성 완료${inFlightSuffix}`);
      } else if (completedStudents > 0) {
        setServerMessage(`${totalStudents}명 중 ${completedStudents}명 완료${inFlightSuffix || ' · 다음 학생들을 작성하고 있어요'}`);
      } else if (inFlight > 0) {
        setServerMessage(`${totalStudents}명 중 ${inFlight}명을 동시에 작성하고 있어요`);
      } else {
        setServerMessage(`${totalStudents}명 학생별 생기부 문구를 작성하고 있어요`);
      }
    }
  }, [applyServerCounts, setServerMessage]);

  const createGenerationCancelledError = useCallback((message = '생성을 중단했어요.') => {
    const error = new Error(message);
    error.isGenerationCancelled = true;
    return error;
  }, []);

  const handleCancelGeneration = useCallback(async () => {
    if (!isProcessing || isCancellingGeneration) return;

    generationCancelRequestedRef.current = true;
    setIsCancellingGeneration(true);
    setCurrentStudent('생성을 중단하고 있어요.');

    const jobId = generationJobIdRef.current;
    generationAbortControllerRef.current?.abort();

    if (!jobId) {
      showToast('생성 중단을 준비하고 있어요. 잠시만 기다려주세요.', 'info');
      return;
    }

    try {
      await cancelSaenggibuGeneration(jobId);
      showToast('생성 중단 요청을 보냈어요.', 'info');
    } catch (error) {
      console.error('생성 중단 요청 실패:', error);
      showToast('생성 중단 요청을 보내지 못했어요. 다시 눌러주세요.', 'warning');
      setIsCancellingGeneration(false);
    }
  }, [isProcessing, isCancellingGeneration, showToast]);

  useEffect(() => {
    isMountedRef.current = true;
    if (!hasStarted.current) {
      hasStarted.current = true;
      // 컴포넌트가 마운트되면 자동으로 생성 시작
      startBatchGeneration();
    }
    
    // 클린업
    return () => {
      isMountedRef.current = false;
      hasStarted.current = false;
      generationRunIdRef.current += 1;
      generationCancelRequestedRef.current = true;
      generationAbortControllerRef.current?.abort();
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
      const activeJobId = generationJobIdRef.current;
      if (activeJobId) {
        Promise.resolve(cancelSaenggibuGeneration(activeJobId)).catch(error => {
          console.warn('화면 이탈 중 다중 생성을 중단하지 못했어요:', error);
        });
      }
      stopProgressSimulation();
    };
  }, [stopProgressSimulation]); // eslint-disable-line react-hooks/exhaustive-deps

  // 완료 결과를 화면 전환으로 마무리 (정상 완료/복구 완료 공용)
  const finishWithResult = useCallback((result, students, options, runId) => {
    if (!isMountedRef.current || generationRunIdRef.current !== runId) return;
    if (generationCancelRequestedRef.current || result?.cancelled || result?.data?.cancelled) {
      throw createGenerationCancelledError(result?.message || result?.data?.message);
    }
    const historySaved = Boolean(result?.history_saved || result?.data?.history_saved);
    if (historySaved) {
      window.dispatchEvent(new CustomEvent('saenggibuCreated'));
    }

    stopProgressSimulation();
    generationAbortControllerRef.current = null;
    generationJobIdRef.current = null;

    setProgress(100);
    setServerCounts(prev => (prev ? { ...prev, completed: prev.total, inFlight: 0 } : prev));
    setCurrentStudent(`${students.length}명 생기부 완성! 확인해보세요.`);

    completionTimeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current || generationRunIdRef.current !== runId) return;
      const batchInputContext = {
        students,
        teacher_instruction: options.teacherInstruction || '',
        reference_text: options.referenceText || '',
        reference_files_count: (options.referenceFiles || []).length,
        source_file_meta: analyzedData.metadata?.source_file_meta || {},
        structure_analysis: analyzedData.metadata?.structure_analysis || {},
      };
      onNext({
        ...(result.data || {}),
        record_id: result.record_id || result.data?.record_id,
        history_saved: historySaved,
        history_error: result.history_error || result.data?.history_error,
        options,
        batch_input_context: batchInputContext,
      });
      completionTimeoutRef.current = null;
    }, 1000);
  }, [analyzedData, onNext, stopProgressSimulation, createGenerationCancelledError]);

  /**
   * 스트림 연결이 끝내 끊겼을 때의 자동 복구:
   * 서버는 연결과 무관하게 생성을 계속하므로, 가벼운 상태 조회로 완료를
   * 기다렸다가 같은 jobId 로 재요청해 저장된 결과를 회수한다 (중복 생성 없음).
   */
  const recoverViaJobStatus = useCallback(async ({ jobId, students, options, metadata, runId }) => {
    if (!jobId || !currentUser) return false;
    const startedAt = Date.now();
    setServerMessage('연결이 불안정해요. 서버에 저장된 결과를 확인하고 있어요.', RECOVERY_MAX_WAIT_MS);

    while (Date.now() - startedAt < RECOVERY_MAX_WAIT_MS) {
      if (!isMountedRef.current || generationRunIdRef.current !== runId) return true;
      if (generationCancelRequestedRef.current) return false;

      let status = null;
      try {
        status = await getSaenggibuGenerationJobStatus(jobId);
      } catch (statusError) {
        console.warn('생성 상태 조회 실패 (잠시 후 재시도):', statusError?.message);
      }

      if (status) {
        if (status.status === 'completed' && status.record_id) {
          try {
            setServerMessage('생성이 완료되어 있어요. 결과를 불러오고 있어요.', RECOVERY_MAX_WAIT_MS);
            // 같은 jobId 재요청 → 서버 멱등성 가드가 저장된 결과를 즉시 돌려줌
            const replay = await generateBatchSaenggibuStream(
              students,
              options,
              () => currentUser.getIdToken(),
              metadata,
              {}
            );
            finishWithResult(replay, students, options, runId);
            return true;
          } catch (replayError) {
            console.warn('저장된 결과 회수 실패:', replayError?.message);
            stopProgressSimulation();
            generationAbortControllerRef.current = null;
            showToast('생성은 완료되어 생기부 기록에 저장됐어요. 기록에서 확인해주세요.', 'info', 8000);
            setServerMessage('생성이 완료되어 생기부 기록에 저장됐어요. 기록 메뉴에서 확인해주세요.', RECOVERY_MAX_WAIT_MS);
            setIsProcessing(false);
            setIsCancellingGeneration(false);
            return true;
          }
        }
        if (status.status === 'failed' || status.status === 'not_found') {
          return false;
        }
        if (status.status === 'cancelled') {
          stopProgressSimulation();
          generationAbortControllerRef.current = null;
          showToast('생성을 중단했어요.', 'info');
          setServerMessage('생성을 중단했어요.', RECOVERY_MAX_WAIT_MS);
          setIsProcessing(false);
          setIsCancellingGeneration(false);
          return true;
        }
        // running → 서버가 계속 만드는 중. 잡 문서의 진행 스냅샷이 있으면 인원수도 갱신한다.
        const snapshot = status.progress || {};
        const applied = applyServerCounts({
          completed: snapshot.completed_students,
          total: snapshot.total_students,
          inFlight: snapshot.in_flight_students,
        });
        if (applied) lastServerEventAtRef.current = Date.now();
        setServerMessage(
          applied
            ? `${snapshot.total_students}명 중 ${snapshot.completed_students}명 완료 · 네트워크가 불안정하지만 서버에서 계속 작성 중이에요`
            : '네트워크가 불안정하지만 서버에서 생성이 계속 진행 중이에요. 잠시만 기다려주세요.',
          RECOVERY_MAX_WAIT_MS
        );
      }

      await new Promise(resolve => setTimeout(resolve, RECOVERY_POLL_INTERVAL_MS));
    }
    return false;
  }, [applyServerCounts, currentUser, finishWithResult, setIsProcessing, setServerMessage, showToast, stopProgressSimulation]);

  const startBatchGeneration = async () => {
    if (!analyzedData?.analysisResult?.students || !currentUser) {
      showToast('데이터가 없어요', 'error');
      return;
    }

    const runId = generationRunIdRef.current + 1;
    generationRunIdRef.current = runId;

    const students = analyzedData.analysisResult.students;
    const options = selectedOptions || {};
    const generationJobId = createBatchGenerationJobId();
    const metadata = {
      ...(analyzedData.metadata || {}),
      generation_job_id: generationJobId
    };

    try {
      setIsProcessing(true);
      setIsCancellingGeneration(false);
      generationCancelRequestedRef.current = false;

      const abortController = new AbortController();
      generationJobIdRef.current = generationJobId;
      generationAbortControllerRef.current = abortController;
      startProgressSimulation(students.length);

      // 백엔드 하트비트 스트림으로 일괄 생성 (연결 유실 시 같은 jobId 로 자동 재접속)
      const result = await generateBatchSaenggibuStream(
        students,
        options,
        () => currentUser.getIdToken(),
        metadata,
        { signal: abortController.signal, onProgress: handleServerProgress }
      );
      finishWithResult(result, students, options, runId);

    } catch (error) {
      if (!isMountedRef.current || generationRunIdRef.current !== runId) return;
      console.error('생성 실패:', error);
      if (
        generationCancelRequestedRef.current ||
        error.isGenerationCancelled ||
        error.name === 'AbortError'
      ) {
        stopProgressSimulation();
        generationAbortControllerRef.current = null;
        showToast('생성을 중단했어요. 기존 결과는 그대로 유지했어요.', 'info');
        setCurrentStudent('생성을 중단했어요.');
        setIsProcessing(false);
        setIsCancellingGeneration(false);
        generationCancelRequestedRef.current = false;
        return;
      }

      // 연결이 끊겨도 서버는 생성을 계속했을 수 있다 → 저장된 결과 자동 복구 시도
      const recovered = await recoverViaJobStatus({
        jobId: generationJobId,
        students,
        options,
        metadata,
        runId,
      });
      if (recovered) return;
      if (!isMountedRef.current || generationRunIdRef.current !== runId) return;

      stopProgressSimulation();
      generationAbortControllerRef.current = null;
      showToast(getFriendlyBatchErrorMessage(error), 'error');
      setCurrentStudent('생성에 실패했어요. 네트워크 상태를 확인한 뒤 이전 단계에서 다시 시도해주세요.');
      setIsProcessing(false);
      setIsCancellingGeneration(false);
    }
  };

  return (
    <div className="saenggibu-batch-processing">
      <div className="saenggibu-batch-step-header">
        <h2>생기부를 작성하고 있어요</h2>
        <p className="saenggibu-batch-step-description">
          {formatEstimatedDuration(estimatedSeconds) || '학생 수에 따라 1~5분 정도 걸려요'}
        </p>
      </div>

      <div className="saenggibu-batch-processing-content">
        {/* 진행률 표시 — 서버 인원 카운트가 있으면 검사기와 같은 "N/M명 완료", 없으면 % */}
        <div className="saenggibu-batch-progress-section">
          <div className="saenggibu-batch-progress-info">
            <span className="saenggibu-batch-progress-label">작성 중</span>
            <span className="saenggibu-batch-progress-percentage">
              {serverCounts
                ? `${serverCounts.completed}/${serverCounts.total}명 완료`
                : `${progress}%`}
            </span>
          </div>
          <div className="saenggibu-batch-progress-bar">
            <div 
              className="saenggibu-batch-progress-fill" 
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="saenggibu-batch-progress-note">{currentStudent}</p>
          {elapsedSeconds >= 60 && progress < 100 && (
            <p className="saenggibu-batch-progress-subnote">
              {Math.floor(elapsedSeconds / 60)}분 {elapsedSeconds % 60}초째 작성 중이에요. 학생 수가 많거나 문장을 길게 다듬을 때 더 걸릴 수 있어요.
            </p>
          )}
          {isProcessing && (
            <button
              type="button"
              className="saenggibu-batch-cancel-generation-btn"
              onClick={handleCancelGeneration}
              disabled={isCancellingGeneration}
            >
              {isCancellingGeneration ? '중단 중' : '생성 중단'}
            </button>
          )}
        </div>
        <BatchLoadingMiniGame label="문장 기다리는 동안 점프!" />
      </div>

      <div className="saenggibu-batch-step-navigation">
        <button 
          className="saenggibu-batch-nav-btn saenggibu-batch-back-btn"
          onClick={onBack}
          disabled={isProcessing}
        >
          이전
        </button>
        
        <button 
          className="saenggibu-batch-nav-btn saenggibu-batch-next-btn"
          onClick={() => onNext({})}
          disabled={isProcessing}
        >
          완료
        </button>
      </div>
    </div>
  );
};

export default Step4_Processing;
